/**
 * Time Parsing - Source-specific timestamp parsing
 * All parsers return: Date object (UTC) or null on failure
 *
 * SYNC: This module is shared between frontend and backend.
 */

/**
 * Parse NOAA timestamp format: "YYYY-MM-DD HH:mm"
 * NOAA returns local time by default, but we request GMT from backend
 *
 * @param {string} noaaTimestamp - e.g., "2024-01-15 14:30"
 * @param {boolean} isGMT - Whether the timestamp is in GMT (default: true for backend)
 * @returns {Date|null}
 */
export function parseNOAATimestamp(noaaTimestamp, isGMT = true) {
    if (!noaaTimestamp) return null;

    try {
        // Replace space with T for ISO-8601 compatibility
        const isoString = noaaTimestamp.replace(' ', 'T');

        // Add timezone indicator
        const fullString = isGMT ? `${isoString}Z` : isoString;

        const date = new Date(fullString);
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

/**
 * Parse NDBC buoy timestamp from data columns
 * Format: YY MM DD hh mm (or YYYY MM DD hh mm)
 * Always UTC
 *
 * @param {string[]} parts - Array of [year, month, day, hour, minute]
 * @returns {Date|null}
 */
export function parseNDBCTimestamp(parts) {
    if (!parts || parts.length < 5) return null;

    try {
        // Handle both 2-digit and 4-digit years
        const year = parts[0].length === 4 ? parts[0] : `20${parts[0]}`;
        const timestamp = `${year}-${parts[1]}-${parts[2]}T${parts[3]}:${parts[4]}:00Z`;
        const date = new Date(timestamp);
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

/**
 * Parse CDIP timestamp (Unix timestamp in seconds)
 * CDIP API returns waveTime in seconds, not milliseconds
 *
 * @param {number} unixSeconds - Unix timestamp in seconds
 * @returns {Date|null}
 */
export function parseCDIPTimestamp(unixSeconds) {
    if (typeof unixSeconds !== 'number' || unixSeconds <= 0) return null;

    const date = new Date(unixSeconds * 1000);
    return isNaN(date.getTime()) ? null : date;
}

/**
 * Parse ISO 8601 timestamp (standard format from APIs)
 *
 * @param {string|Date} input - ISO string or Date object
 * @returns {Date|null}
 */
export function parseISO(input) {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;

    try {
        const date = new Date(input);
        return isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

/**
 * Parse Unix timestamp in milliseconds
 *
 * @param {number} ms - Unix timestamp in milliseconds
 * @returns {Date|null}
 */
export function parseUnixMs(ms) {
    if (typeof ms !== 'number' || ms <= 0) return null;

    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
}

/**
 * Parse any supported timestamp format (auto-detect)
 *
 * @param {string|number|Date} input - Timestamp in any format
 * @returns {Date|null}
 */
export function parseAny(input) {
    if (!input) return null;

    // Already a Date
    if (input instanceof Date) {
        return isNaN(input.getTime()) ? null : input;
    }

    // Number = Unix ms
    if (typeof input === 'number') {
        return parseUnixMs(input);
    }

    // String parsing
    if (typeof input === 'string') {
        // Check for NOAA format (space separator, no T)
        if (input.includes(' ') && !input.includes('T')) {
            // Try GMT first (most common in our backend)
            const parsed = parseNOAATimestamp(input, true);
            if (parsed) return parsed;
        }

        // Default to ISO parsing
        return parseISO(input);
    }

    return null;
}

/**
 * Get date range for API queries
 *
 * @param {'predictions' | 'curve' | 'historical'} type - Type of date range
 * @param {Date} now - Reference date (default: current time)
 * @returns {{ start: Date, end: Date }}
 */
export function getDateRange(type, now = new Date()) {
    const MS_PER_HOUR = 60 * 60 * 1000;
    const MS_PER_DAY = 24 * MS_PER_HOUR;

    switch (type) {
        case 'predictions':
            return {
                start: new Date(now.getTime() - 12 * MS_PER_HOUR),
                end: new Date(now.getTime() + 2 * MS_PER_DAY),
            };
        case 'curve':
            return {
                start: new Date(now.getTime() - 12 * MS_PER_HOUR),
                end: new Date(now.getTime() + 12 * MS_PER_HOUR),
            };
        case 'historical':
            return {
                start: new Date(now.getTime() - 12 * MS_PER_HOUR),
                end: now,
            };
        default:
            return {
                start: new Date(now.getTime() - 12 * MS_PER_HOUR),
                end: new Date(now.getTime() + 2 * MS_PER_DAY),
            };
    }
}

/**
 * Calculate age of a timestamp in minutes
 * Replaces patterns like: Math.round((now() - new Date(updatedAt)) / 60000)
 *
 * @param {Date|string|number} timestamp - The timestamp to measure age from
 * @param {Date} relativeTo - Reference time (default: current time)
 * @returns {number|null} Age in minutes (rounded), or null if timestamp invalid
 */
export function ageInMinutes(timestamp, relativeTo = new Date()) {
    const date = parseAny(timestamp);
    if (!date) return null;

    const ageMs = relativeTo - date;
    return Math.round(ageMs / 60000);
}

/**
 * Calculate minutes until a future timestamp
 * Replaces patterns like: (futureDate - now()) / (1000 * 60)
 *
 * @param {Date|string|number} timestamp - The future timestamp
 * @param {Date} relativeTo - Reference time (default: current time)
 * @returns {number|null} Minutes until timestamp (rounded), or null if timestamp invalid
 */
export function minutesUntil(timestamp, relativeTo = new Date()) {
    const date = parseAny(timestamp);
    if (!date) return null;

    const diffMs = date - relativeTo;
    return Math.round(diffMs / 60000);
}

// ===========================================
// Duration Breakdown Utilities
// ===========================================

/**
 * Break down total minutes into hours and minutes
 * Replaces patterns like: Math.floor(mins / 60), mins % 60
 *
 * @param {number} totalMinutes - Total minutes
 * @returns {{ hours: number, minutes: number }}
 */
export function breakdownMinutes(totalMinutes) {
    if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes)) {
        return { hours: 0, minutes: 0 };
    }
    const total = Math.abs(Math.round(totalMinutes));
    return {
        hours: Math.floor(total / 60),
        minutes: total % 60,
    };
}

/**
 * Break down total seconds into hours, minutes, and seconds
 * Replaces patterns like: Math.floor(secs / 3600), Math.floor((secs % 3600) / 60)
 *
 * @param {number} totalSeconds - Total seconds
 * @returns {{ hours: number, minutes: number, seconds: number }}
 */
export function breakdownSeconds(totalSeconds) {
    if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) {
        return { hours: 0, minutes: 0, seconds: 0 };
    }
    const total = Math.abs(Math.round(totalSeconds));
    return {
        hours: Math.floor(total / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60,
    };
}

// ===========================================
// Unit Conversion Utilities
// ===========================================

/** Milliseconds per second */
export const MS_PER_SECOND = 1000;
/** Milliseconds per minute */
export const MS_PER_MINUTE = 60 * 1000;
/** Milliseconds per hour */
export const MS_PER_HOUR = 60 * 60 * 1000;
/** Milliseconds per day */
export const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Seconds per minute */
export const SECONDS_PER_MINUTE = 60;
/** Seconds per hour */
export const SECONDS_PER_HOUR = 3600;
/** Seconds per day */
export const SECONDS_PER_DAY = 86400;

/**
 * Convert seconds to milliseconds
 * @param {number} seconds
 * @returns {number}
 */
export function secondsToMs(seconds) {
    return seconds * MS_PER_SECOND;
}

/**
 * Convert milliseconds to seconds
 * @param {number} ms
 * @returns {number}
 */
export function msToSeconds(ms) {
    return Math.floor(ms / MS_PER_SECOND);
}

/**
 * Convert minutes to milliseconds
 * @param {number} minutes
 * @returns {number}
 */
export function minutesToMs(minutes) {
    return minutes * MS_PER_MINUTE;
}

/**
 * Convert milliseconds to minutes
 * @param {number} ms
 * @returns {number}
 */
export function msToMinutes(ms) {
    return Math.floor(ms / MS_PER_MINUTE);
}

/**
 * Convert days to milliseconds
 * @param {number} days
 * @returns {number}
 */
export function daysToMs(days) {
    return days * MS_PER_DAY;
}

/**
 * Convert hours to milliseconds
 * @param {number} hours
 * @returns {number}
 */
export function hoursToMs(hours) {
    return hours * MS_PER_HOUR;
}

/**
 * Calculate age of a timestamp in seconds
 * @param {Date|string|number} timestamp - The timestamp to measure age from
 * @param {Date} relativeTo - Reference time (default: current time)
 * @returns {number|null} Age in seconds (rounded), or null if timestamp invalid
 */
export function ageInSeconds(timestamp, relativeTo = new Date()) {
    const date = parseAny(timestamp);
    if (!date) return null;

    const ageMs = relativeTo - date;
    return Math.round(ageMs / MS_PER_SECOND);
}

/**
 * Parse a time string in HH:MM:SS format to total seconds
 * Replaces patterns like: parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2])
 *
 * @param {string} timeString - Time in "HH:MM:SS" or "H:MM:SS" format
 * @returns {number|null} Total seconds, or null if invalid
 */
export function parseTimeString(timeString) {
    if (!timeString || typeof timeString !== 'string') return null;

    const parts = timeString.split(':');
    if (parts.length !== 3) return null;

    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);

    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;

    return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
}

/**
 * Create a Date offset from a reference time
 * Replaces patterns like: new Date(currentTime.getTime() - DATE_RANGES.historicalLookback)
 *
 * @param {number} ms - Offset in milliseconds (positive = future, negative = past)
 * @param {Date} relativeTo - Reference time (default: current time)
 * @returns {Date}
 */
export function dateOffset(ms, relativeTo = new Date()) {
    const refDate = parseAny(relativeTo);
    if (!refDate) return new Date(Date.now() + ms);
    return new Date(refDate.getTime() + ms);
}

/**
 * Parse sun times from API response or stored data
 * Handles both sunrise-sunset.org format and internal storage format
 *
 * @param {Object} data - Sun times data object
 * @param {string} data.sunrise - Sunrise timestamp (ISO string)
 * @param {string} data.sunset - Sunset timestamp (ISO string)
 * @param {string} [data.solarNoon] - Solar noon timestamp
 * @param {string} [data.solar_noon] - Solar noon (alternative key from API)
 * @param {string|number} [data.dayLength] - Day length in seconds or "HH:MM:SS"
 * @param {string|number} [data.day_length] - Day length (alternative key)
 * @returns {{ sunrise: Date, sunset: Date, solarNoon: Date, dayLength: number|string }|null}
 */
export function parseSunTimes(data) {
    if (!data) return null;

    const sunrise = parseISO(data.sunrise);
    const sunset = parseISO(data.sunset);

    if (!sunrise || !sunset) return null;

    const solarNoon = parseISO(data.solarNoon || data.solar_noon);
    const dayLength = data.dayLength ?? data.day_length ?? null;

    return {
        sunrise,
        sunset,
        solarNoon,
        dayLength,
    };
}
